import {put, call, all} from 'redux-saga/effects';
import ImportExport from "../../entities/importExport";
import * as Actions from "../actions/importExportActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type ImportExportAction = {
    importExportId: string;
    importExport: ImportExport;
    importExports: ImportExport[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneImportExport(action: ImportExportAction) {
	const session = tabSession.captureGeneration();
	try {
		const importExport: ImportExport = yield call(api.findOne, 'importExport', action.importExportId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.IMPORTEXPORT_FETCH_ONE_SUCCESS, importExportId: action.importExportId, importExport, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.IMPORTEXPORT_FETCH_ONE_FAILURE, importExportId: action.importExportId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {ImportExportAction} action
 */
export function* saveImportExport(action: ImportExportAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ IMPORTEXPORT_SAVE saga', action.importExport?.id);

		if (action.importExport === undefined || action.importExport === null) {
			console.error('saveImportExport received a null');
			throw new Error('saveImportExport received a null');
		}

		if (!(action.importExport instanceof ImportExport)) {
			action.importExport = new ImportExport(action.importExport);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.importExport.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.importExport);
			const addResult = yield call(idb.add, 'importExport', action.importExport);
			if (addResult.isErr()) {
				console.error(`⮂ saveImportExport: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertImportExportSave(action.importExport, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.IMPORTEXPORT_SAVE_FAILURE, importExport: action.importExport, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.IMPORTEXPORT_SAVE, importExport: action.importExport, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertImportExportSave(action.importExport, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.IMPORTEXPORT_SAVE_FAILURE, importExport: action.importExport, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {ImportExportAction} action
 * @param {ImportExport[]} action.importExports
 */
export function* saveManyImportExports(action: ImportExportAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ IMPORTEXPORT_SAVE_MANY saga', action.importExports.map(importExport => importExport.id).join(','));

        if (!Array.isArray(action.importExports)) {
            console.error('saveManyImportExports expected an array, received', action.importExports);
            throw new Error('saveManyImportExports expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveImportExport), under these queue keys
		const dispatchedAddedOns: number[] = action.importExports.map(importExport => importExport?.addedOn);
		const revertAll = () => action.importExports.map((importExport, i) =>
			put(Actions.revertImportExportSave(importExport, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: ImportExport[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.importExports.length; i++) {
				const importExport = action.importExports[i];
				importExport.persisted = false;
				ensureOperationId(importExport);
				toSave.push(importExport);
			}

			const addResult = yield call(idb.add, 'importExport', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyImportExports: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.IMPORTEXPORT_SAVE_MANY_FAILURE, importExports: action.importExports, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.IMPORTEXPORT_SAVE_MANY, importExports: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.IMPORTEXPORT_SAVE_MANY_FAILURE, importExports: action.importExports, status: 500, err});

			console.error(`Error saving ${action.importExports.length} importExports to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {ImportExportAction} action
 */
export function* removeImportExport(action: ImportExportAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.importExport === undefined || action.importExport === null || !(action.importExport instanceof ImportExport)) {
			console.error('removeImportExport expected type ImportExport, received', action.importExport);
			reportQueued(false);
			throw new Error('removeImportExport expected type ImportExport');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.importExport.status = 'D';
		action.importExport.addedOn = newHRTimestamp();
		action.importExport.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.importExport);
		const addResult = yield call(idb.add, 'importExport', action.importExport);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeImportExport: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.IMPORTEXPORT_REMOVE_FAILURE, importExport: action.importExport, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.IMPORTEXPORT_REMOVE, importExport: action.importExport});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.IMPORTEXPORT_REMOVE_FAILURE, importExport: action.importExport, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this ImportExport.
	*
	* @param {ImportExportAction} action
	*/
export function* restoreImportExport(action: ImportExportAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreImportExport expected getCurrentUserId() to return a value');
        return;
    }

	if (action.importExport === undefined || action.importExport === null) {
        console.error('restoreImportExport expected a ImportExport, received null');
        throw new Error('restoreImportExport expected a ImportExport, received null');
    }
    else if (!(action.importExport instanceof ImportExport)) action.importExport = new ImportExport(action.importExport); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.importExport.status = 'U';
		action.importExport.addedOn = newHRTimestamp();
		action.importExport.addedBy = userId;
		action.importExport.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.importExport);
		const addResult = yield call(idb.add, 'importExport', action.importExport);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreImportExport: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.IMPORTEXPORT_RESTORE_FAILURE, importExport: action.importExport, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.IMPORTEXPORT_RESTORE, importExport: action.importExport});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.IMPORTEXPORT_RESTORE_FAILURE, importExport: action.importExport, status: 500, err});

		console.error('Error restoring from database', err);
	}
}